import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { ReactNode } from 'react';

/**
 * How much typed work has not reached the API yet — GEN-55.
 *
 * The console saves on blur, so "unsaved" is narrow: text typed into a field the reviewer
 * has not left. Narrow enough that it was never shown, which is the problem — an operator
 * closing the tab after six corrections had no way to know whether all six landed.
 *
 * Two contexts rather than one object: every field subscribes to `mark` and only the
 * indicator subscribes to the count, so one field going dirty does not re-render the
 * other forty.
 */

/** Called by `useDraft`: +1 when a field starts holding work, -1 when it stops. */
const Mark = createContext<(delta: number) => void>(() => undefined);

const Count = createContext(0);

export function UnsavedProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);

  const mark = useCallback((delta: number) => {
    setCount((held) => held + delta);
  }, []);

  const holding = count > 0;

  /**
   * The guard — GEN-55.
   *
   * `beforeunload` and nothing else, because in-app navigation cannot reach this state:
   * every route out of the workspace is a click, a click blurs the field, and blur is what
   * commits. Closing the tab and reloading are the two ways to leave without a blur.
   */
  useEffect(() => {
    if (!holding) return;

    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', guard);
    return () => {
      window.removeEventListener('beforeunload', guard);
    };
  }, [holding]);

  return (
    <Mark.Provider value={mark}>
      <Count.Provider value={count}>{children}</Count.Provider>
    </Mark.Provider>
  );
}

/**
 * Reporting is optional, unlike `useActions`. `useDraft` is also used by screens with no
 * such indicator — `CourseDetail` — and throwing there would make a field's dirtiness
 * something every future screen has to opt into before it can render one.
 */
export const useMarkUnsaved = (): ((delta: number) => void) => useContext(Mark);

export const useUnsavedCount = (): number => useContext(Count);
